import { fetchWithAuth } from './auth';

export interface FieldPhotoDeviceInfo {
  source?: string;
  import_intent?: 'upload_only' | 'label_now' | 'group_first' | string;
  original_taken_at?: string | null;
  original_filename?: string;
  label_status?: 'unlabeled' | 'labeled' | string;
  context?: {
    category?: string;
    label?: string;
    description?: string;
    elevation?: string | null;
  };
  damage_status?: 'damage' | 'no_damage' | string;
  flagged?: boolean;
  note_text?: string;
  gps?: { lat: number; lng: number; accuracy_m: number } | null;
  ua?: string;
}

export interface FieldPhoto {
  id: string;
  job_id: string;
  session_id: string;
  user_id: string;
  sequence_index: number;
  storage_path: string;
  captured_at: string | null;
  confirmed_at: string | null;
  checksum: string;
  device_info: FieldPhotoDeviceInfo;
}

export async function listFieldPhotosForJob(jobId: string): Promise<FieldPhoto[]> {
  const r = await fetchWithAuth(`/api/field/photos/by-job/${jobId}`);
  if (!r.ok) throw new Error(`listFieldPhotosForJob: ${r.status}`);
  return (await r.json()) as FieldPhoto[];
}

export async function getFieldPhotoSignedUrl(photoId: string): Promise<string> {
  const r = await fetchWithAuth(`/api/field/photos/${photoId}/signed-url`);
  if (!r.ok) throw new Error(`getFieldPhotoSignedUrl: ${r.status}`);
  const data = (await r.json()) as { url: string };
  return data.url;
}

export interface FieldPermissions {
  allow_inspector_delete: boolean;
}

export async function getFieldPermissions(): Promise<FieldPermissions> {
  const r = await fetchWithAuth('/api/field/photos/permissions');
  if (!r.ok) throw new Error(`getFieldPermissions: ${r.status}`);
  return (await r.json()) as FieldPermissions;
}

export async function deleteFieldPhoto(
  photoId: string,
  reason?: string,
): Promise<void> {
  const r = await fetchWithAuth(`/api/field/photos/${photoId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? null }),
  });
  if (!r.ok) {
    throw new Error(`deleteFieldPhoto: ${r.status}: ${(await r.text()).slice(0, 200)}`);
  }
}

export async function renumberSequence(
  jobId: string,
  choice: 'renumber' | 'gap_preserved',
): Promise<{ final_count: number; gaps_before: number[] }> {
  const r = await fetchWithAuth('/api/field/photos/renumber', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ job_id: jobId, choice }),
  });
  if (!r.ok) {
    throw new Error(`renumberSequence: ${r.status}: ${(await r.text()).slice(0, 200)}`);
  }
  return (await r.json()) as { final_count: number; gaps_before: number[] };
}

export function categoryDotClass(category: string | null | undefined): string {
  const c = (category ?? '').toLowerCase();
  if (c === 'risk') return 'bg-bracket-amber';
  if (c === 'exterior' || c === 'roof' || c === 'other structures') return 'bg-bracket-blue';
  if (c === 'interior' || c === 'attic') return 'bg-eva-teal';
  return 'bg-gray-400';
}
